import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import apiClient from "../../services/apiClient";
import { getCompletedOrdersWithinDateRange, getCoupons, getOrderTotals, getPendingVendors, getProductTotals, getVendorsTotal } from "../../services/woocommerce";
import { statCards } from "../../constants/ConstantTest";
import LoadingScreen from "../../components/LoadingScreen";
import ErrorComponent from "../../components/ErrorComponent";
import AppointmentsTab from "./section/AppointmentSection";
import UserStatisticsTab from "./section/UserStatisticsSection";
import OrdersTab from "./section/OrderStatisticsSection";

interface GroupedRevenue {
  date: string;
  revenue: number;
}

export default function Dashboard() {
  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [serviceappointments, setserviceAppointments] = useState<any[]>([]);
  const [appointmentsData, setAppointmentsData] = useState<any[]>([]);
  const [serviceappointmentsData, setserviceAppointmentsData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState("veterinarians");
  const [approvals, setApprovals] = useState<any[]>([]);
  const [parents, setParents] = useState<any[]>([]);
  const [coupons, setCoupons] = useState<any[]>([]);
  const [orderTotal, setOrderTotal] = useState<any[]>([]);
  const [productTotal, setproductTotal] = useState<any[]>([]);
  const [activeTabs, setActiveTabs] = useState('user');

  const today = new Date();
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(today.getDate() - 3000);

  const todayDate = new Date().toISOString().split("T")[0];
  const todayAppointments = appointments.filter(appt => appt.scheduled_at.startsWith(todayDate));

  const formatDate = (date: Date, endOfDay = false): string => {
    const pad = (num: number) => String(num).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const time = endOfDay ? "23:59:59" : "00:00:00";

    return `${year}-${month}-${day}T${time}`;
  };

  const startDate = formatDate(fourteenDaysAgo);
  const endDate = formatDate(today, true);



  const fetchDashboardAndAppointments = async () => {
    setLoading(true);
    setError(null);

    const fetchData = async (url: string) => {
      try {
        const res = await apiClient<any>(url, { method: "GET" });
        return res?.data;
      } catch (err) {
        console.error(`Failed to fetch ${url}:`, err);
        return null; // return null instead of throwing
      }
    };

    try {
      const dashboardData = await fetchData("v1/admin/dashboard");
      const vendorCount = await getVendorsTotal().catch((err) => {
        console.error("Failed to fetch vendors:", err);
        return 0;
      });

      setData({ ...dashboardData, vendors: vendorCount });

      const consultsData = await fetchData("v1/resource/consults?limit=1000");
      setAppointments(consultsData?.consults || []);

      const serviceOrdersData = await fetchData("v1/resource/serviceOrders/all");
      setserviceAppointments(serviceOrdersData?.service_orders || []);

      const parentsData = await fetchData("v1/resource/patients");
      setParents(parentsData?.patients || []);

      // Coupons, orders, products
      const couponsRes = await getCoupons().catch(() => ({ data: [] }));
      setCoupons(couponsRes.data || []);

      const orderTotalRes = await getOrderTotals().catch(() => ({ data: [] }));
      setOrderTotal(orderTotalRes.data || []);

      const productTotalRes = await getProductTotals().catch(() => ({ data: [] }));
      setproductTotal(productTotalRes.data || []);

      const completedOrdersRes = await getCompletedOrdersWithinDateRange(startDate, endDate).catch(() => ({ data: [] }));
      groupOrdersByDate(completedOrdersRes.data || []);
    } catch (err: any) {
      console.error("Unexpected error:", err);
      setError(err.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardAndAppointments();
  }, []);

  const handleRetry = () => {
    fetchDashboardAndAppointments();
  };

  useEffect(() => {
    if (!appointments?.length) {
      setAppointmentsData([]);
      return;
    }


    // Filter only "Completed" appointments
    const completedAppointments = appointments.filter(
      (appointment) => appointment.consult_current_status?.slug === "ended"
    );

    const reducedData = completedAppointments.reduce<Record<string, { count: number; revenue: number }>>(
      (acc, appointment) => {
        const day = appointment.scheduled_at.split(" ")[0];
        const totalAmount = appointment?.additional_info?.payment?.total_amount || 0;

        if (!acc[day]) {
          acc[day] = { count: 0, revenue: 0 };
        }

        acc[day].count += 1;
        acc[day].revenue += totalAmount;

        return acc;
      },
      {}
    );

    const chartData = Object.entries(reducedData)
      .map(([day, { count, revenue }]) => ({
        day,
        appointments: count,
        revenue,
      }))
      .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());

    setAppointmentsData(chartData);
  }, [appointments]);


  useEffect(() => {
    if (!serviceappointments?.length) {
      setserviceAppointmentsData([]);
      return;
    }

    // Filter only completed service appointments
    const completedServiceAppointments = serviceappointments.filter(
      (appointment) => appointment.service_status?.slug === "completed"
    );

    const chartData = Object.values(
      completedServiceAppointments.reduce<Record<string, { serviceOrders: number; revenue: number }>>(
        (acc, { scheduled_at, total_amount }) => {
          const day = scheduled_at.split("T")[0];
          const amount = parseFloat(total_amount || "0");

          if (!acc[day]) {
            acc[day] = { serviceOrders: 0, revenue: 0 };
          }

          acc[day].serviceOrders += 1;
          acc[day].revenue += amount;

          return acc;
        },
        {}
      )
    ).map(({ serviceOrders, revenue }, idx, arr) => {
      const day = Object.keys(
        completedServiceAppointments.reduce<Record<string, any>>((acc, { scheduled_at }) => {
          const d = scheduled_at.split("T")[0];
          acc[d] = true;
          return acc;
        }, {})
      )[idx];

      return { day, serviceOrders, revenue: parseFloat(revenue.toFixed(2)) };
    });

    chartData.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());

    setserviceAppointmentsData(chartData);
  }, [serviceappointments]);


  useEffect(() => {
    const fetchApprovals = async () => {
      setError(null);

      try {
        const tabConfig: Record<
          string,
          {
            apiCall: () => Promise<any>;
            normalize: (data: any) => any[];
          }
        > = {
          veterinarians: {
            apiCall: () => apiClient("v1/resource/provider", { method: "GET" }),
            normalize: (data: any) =>
              data.providers.map((p: any) => ({ ...p, type: "Veterinarian" })),
          },
          service_providers: {
            apiCall: () => apiClient("v1/resource/service_provider", { method: "GET" }),
            normalize: (data: any) =>
              data.service_providers.map((p: any) => ({ ...p, type: "Service Provider" })),
          },
          vendors: {
            apiCall: () => getPendingVendors(),
            normalize: (data: any) =>
              data.vendors.map((v: any) => ({ ...v, type: "Vendor" })),
          },
        };

        const config = tabConfig[activeTab];

        if (!config) {
          setApprovals([]);
          return;
        }
        const response = await config.apiCall();

        const normalized = config.normalize(response.data);
        setApprovals(normalized);
      } catch (err: any) {
        setError(err.message || "Failed to fetch approvals");
      }
    };

    fetchApprovals();
  }, [activeTab]);

  const groupOrdersByDate = (orders: { date_created: string; total: string | number }[]): GroupedRevenue[] => {
    const revenueMap = orders.reduce((acc, order) => {
      const date = order.date_created.slice(0, 10);
      const total = typeof order.total === "string" ? parseFloat(order.total) : order.total;

      acc.set(date, (acc.get(date) || 0) + total);
      return acc;
    }, new Map<string, number>());

    const result = Array.from(revenueMap.entries())
      .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
      .map(([date, revenue]) => ({ date, revenue }));

    return result;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const fetches = [
          getCoupons(),
          getOrderTotals(),
          getProductTotals(),
          getCompletedOrdersWithinDateRange(startDate, endDate),
        ];
        const [couponsRes, orderTotalRes, productTotalRes, completedOrdersRes] =
          await Promise.all(fetches);
        setCoupons(couponsRes.data);
        setOrderTotal(orderTotalRes.data);
        setproductTotal(productTotalRes.data);
        const completedOrders = completedOrdersRes.data;
        const groupedData = groupOrdersByDate(completedOrders);
      } catch (err: any) {
        setError(err.message || "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  return (
    <Layout>
      {loading && <LoadingScreen message="Loading Dashboard..." layout={false} />}

      {error ? (
        <ErrorComponent error={error || "Unknown error"} onRetry={handleRetry} useLayout={false} />
      ) : (
        <main className="p-2 sm:p-6 lg:p-6 min-h-screen rounded-lg shadow-sm mx-auto max-w-7xl">
          <div className="flex gap-4 mb-6">
            {/* User Button */}
            <button
              onClick={() => setActiveTabs('user')}
              className={`relative px-4 py-2 rounded-xl font-medium transition-all overflow-hidden ${activeTabs === 'user'
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-400/50'
                : 'bg-gray-400 text-gray-900 shadow-md shadow-gray-500/40 hover:brightness-105 hover:scale-105'
                }`}
              style={{
                boxShadow:
                  activeTabs === 'user'
                    ? '0 4px 10px rgba(59,130,246,0.6), inset 0 2px 0 rgba(255,255,255,0.3)'
                    : '0 2px 6px rgba(156,163,175,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              Users
            </button>

            {/* Order Button */}
            <button
              onClick={() => setActiveTabs('order')}
              className={`relative px-4 py-2 rounded-xl font-medium transition-all overflow-hidden ${activeTabs === 'order'
                ? 'bg-green-600 text-white shadow-lg shadow-green-400/50'
                : 'bg-gray-400 text-gray-900 shadow-md shadow-gray-500/40 hover:brightness-105 hover:scale-105'
                }`}
              style={{
                boxShadow:
                  activeTabs === 'order'
                    ? '0 4px 10px rgba(34,197,94,0.6), inset 0 2px 0 rgba(255,255,255,0.3)'
                    : '0 2px 6px rgba(156,163,175,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              Products & Orders
            </button>

            {/* Product Button */}
            {/* <button
            onClick={() => setActiveTabs('product')}
            className={`relative px-4 py-2 rounded-xl font-medium transition-all overflow-hidden ${activeTabs === 'product'
              ? 'bg-red-600 text-white shadow-lg shadow-red-400/50'
              : 'bg-gray-400 text-gray-900 shadow-md shadow-gray-500/40 hover:brightness-105 hover:scale-105'
              }`}
            style={{
              boxShadow:
                activeTabs === 'product'
                  ? '0 4px 10px rgba(239,68,68,0.6), inset 0 2px 0 rgba(255,255,255,0.3)'
                  : '0 2px 6px rgba(156,163,175,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
            }}
          >
            Products
          </button> */}

            {/* Appointments Button */}
            <button
              onClick={() => setActiveTabs('appointments')}
              className={`relative px-4 py-2 rounded-xl font-medium transition-all overflow-hidden ${activeTabs === 'appointments'
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-400/50'
                : 'bg-gray-400 text-gray-900 shadow-md shadow-gray-500/40 hover:brightness-105 hover:scale-105'
                }`}
              style={{
                boxShadow:
                  activeTabs === 'appointments'
                    ? '0 4px 10px rgba(168,85,247,0.6), inset 0 2px 0 rgba(255,255,255,0.3)'
                    : '0 2px 6px rgba(156,163,175,0.4), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              Appointments
            </button>
          </div>


          {activeTabs === 'user' && (
            <UserStatisticsTab
              data={data}
              approvals={approvals}
              parents={parents}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              statCards={statCards}
            />

          )}

          {activeTabs === "order" && (
            <OrdersTab
              activeTab={activeTabs}
              productTotal={productTotal}
              orderTotal={orderTotal}
              coupons={coupons}
            />
          )}

          {activeTabs === 'appointments' && (
            <>
              <AppointmentsTab
                activeTab={activeTabs}
                appointmentsData={appointmentsData}
                serviceAppointmentsData={serviceappointmentsData}
                todayAppointments={todayAppointments}
              />
            </>
          )}

        </main>)}
    </Layout>
  );
}
